import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { getAllTemperatureOptions, groupTemperatureByCenter, transformToSelectOptions } from '~/utils';

import { API_ENDPOINT, RECEIVE_REQUEST_STATUS, RELEASE_CODE } from '~/constants';

import { receiveKeys } from './keys';
import { RETURN_DISPOSAL_SCREEN_NAME, returnDisposalFilterAllResponse } from './useGetReturnDisposalFilterAllQuery';

import type {
  TypeApiResponse,
  TypeGet3plFilterAllResponse,
  TypeReceiveFilterOptions,
  TypeReturnDisposalFilterAllResponse,
} from '~/types/receive.types';

type ScreenName =
  | 'RECEIVING_REQUEST' // 입고요청관리
  | 'ADD_RECEIVING_GOODS' // 입고상품추가
  | 'RECEIVING_PROGRESS' // 입고 현황
  | 'REPORT_HISTORY' // 입고 리포트 관리
  | 'INBOUND_REQUEST_PROGRESS'; // 3PL 입고 현황

/**
 * 필터 옵션 응답 데이터를 SelectOption 형태로 변환
 */
const formatReceiveFilterAllResponse = (data: TypeGet3plFilterAllResponse): TypeReceiveFilterOptions => {
  const groupedTemperatureByCenter = groupTemperatureByCenter(data.clusters, data.warehouses);

  const center = transformToSelectOptions(data.clusters, 'clusterCode', 'clusterName');
  const temperature = getAllTemperatureOptions(groupedTemperatureByCenter);
  const status = transformToSelectOptions(data.statuses, 'statusCode', 'statusName');
  const releaseCode = transformToSelectOptions(data.releaseCodes, 'releaseCode', 'releaseName');
  const keywordType = transformToSelectOptions(data.codeTypes, 'type', 'name');
  const dateType = transformToSelectOptions(data.dateTypes, 'type', 'name');
  const xDock = transformToSelectOptions(data.xdockAllYesNo, 'code', 'name');
  const disallow = transformToSelectOptions(data.disallowAllYesNo, 'code', 'name');
  const supplierTextFormatter = (value: string, text: string) => {
    return `${text}${value ? ' (' + value + ')' : ''}`;
  };
  const supplier = transformToSelectOptions(data.suppliers, 'code', 'name', supplierTextFormatter);
  const vehicleSize = transformToSelectOptions(data.vehicleSizes, 'code', 'name');
  const reportTaskCodes = transformToSelectOptions(data.reportTaskCodes, 'code', 'name');
  const reportStatus = transformToSelectOptions(data.reportStatuses, 'code', 'name');
  const reportAllYesNo = transformToSelectOptions(data.reportAllYesNo, 'code', 'name');

  return {
    groupedTemperatureByCenter,
    center,
    temperature,
    status,
    releaseCode,
    keywordType,
    dateType,
    xDock,
    disallow,
    supplier,
    vehicleSize,
    reportTaskCodes,
    reportStatus,
    reportAllYesNo,
  };
};

/**
 * [입고관리] 필터 옵션 조회 API
 */
export const fetchFilterAll = async (screenName: ScreenName): Promise<TypeReceiveFilterOptions> => {
  const { data } = await axiosInstance.get<TypeApiResponse<TypeGet3plFilterAllResponse>>(
    `${API_ENDPOINT.RECEIVE.GET_FILTER_ALL}?screenName=${screenName}`
  );
  return formatReceiveFilterAllResponse(data.data);
};

/**
 * [입고관리] 필터 옵션 조회 Query Hook
 */
export const useGetFilterAllQuery = (
  screenName: ScreenName,
  options?: { enabled?: boolean; showGlobalErrorToast?: boolean }
) => {
  return useQuery({
    queryKey: receiveKeys.filterAll(screenName),
    queryFn: () => fetchFilterAll(screenName),
    staleTime: 1000 * 60 * 30, // 30분
    enabled: options?.enabled ?? true,
    meta: { showGlobalErrorToast: options?.showGlobalErrorToast ?? true },
  });
};

const filterAllNormalResponse: TypeApiResponse<TypeGet3plFilterAllResponse> = {
  data: {
    clusters: [
      { clusterCode: 'CC01', clusterName: '장지' },
      { clusterCode: 'CC02', clusterName: '김포' },
      { clusterCode: 'CC03', clusterName: '평택' },
      { clusterCode: 'CC04', clusterName: '창원' },
    ],
    warehouses: [
      { warehouseCode: 'WH01_O', warehouseName: '장지상온', clusterCode: 'CC01' },
      { warehouseCode: 'WH01_R', warehouseName: '장지냉장1', clusterCode: 'CC01' },
      { warehouseCode: 'GGH1', warehouseName: '김포상온', clusterCode: 'CC02' },
      { warehouseCode: 'GGM1', warehouseName: '김포냉장', clusterCode: 'CC02' },
      { warehouseCode: 'GGL1', warehouseName: '김포냉동', clusterCode: 'CC02' },
      { warehouseCode: 'GPH1', warehouseName: '평택상온', clusterCode: 'CC03' },
      { warehouseCode: 'GPM1', warehouseName: '평택냉장', clusterCode: 'CC03' },
      { warehouseCode: 'GPL1', warehouseName: '평택냉동', clusterCode: 'CC03' },
      { warehouseCode: 'KCH1', warehouseName: '창원상온', clusterCode: 'CC04' },
    ],
    statuses: Object.entries(RECEIVE_REQUEST_STATUS).map(([statusCode, statusName]) => ({ statusCode, statusName })),
    // KURLY_MILKRUN_PICKUP은 FE 표시용 파생 코드라 BE 응답 예시 데이터에서 제외
    releaseCodes: Object.entries(RELEASE_CODE)
      .filter(([code]) => code !== 'KURLY_MILKRUN_PICKUP')
      .map(([releaseCode, releaseName]) => ({ releaseCode, releaseName })),
    codeTypes: [
      { type: 'RECEIVING_REQUEST_CODE', name: '입고요청번호' },
      { type: 'SUPPLIER_NAME', name: '공급사명' },
      { type: 'SUPPLIER_CODE', name: '공급사코드' },
      { type: 'GOODS_CODE', name: '상품코드' },
      { type: 'GOODS_NAME', name: '상품명' },
      { type: 'LOT_NUMBER', name: 'LOT번호' },
    ],
    dateTypes: [
      { type: 'RECEIVING_EXPECTED_DATE', name: '입고예정일' },
      { type: 'RECEIVING_REQUESTED_DATE', name: '요청등록일' },
      { type: 'RECEIVING_DATE', name: '입고확정일' },
    ],
    xdockAllYesNo: [
      { code: 'ALL', name: '전체' },
      { code: 'YES', name: '경유함' },
      { code: 'NO', name: '경유안함' },
    ],
    disallowAllYesNo: [
      { code: 'ALL', name: '전체' },
      { code: 'YES', name: '있음' },
      { code: 'NO', name: '없음' },
    ],
    suppliers: [
      { code: 'SU000004', name: '테스트 화주사_첫번째 공급사' },
      { code: 'SU000013', name: '테스트공급사' },
    ],
    vehicleSizes: [
      { code: 'ONE_TON', name: '1톤 이하' },
      { code: 'THREE_HALF_TON', name: '3.5톤 이하' },
      { code: 'FIVE_TON', name: '5톤 이하' },
    ],
    reportTaskCodes: [
      { code: null, name: '전체' },
      { code: 'INB-001-SJ', name: '실셈 (전수검사)' },
      { code: 'INB-002-G', name: '긴급입고' },
    ],
    reportStatuses: [
      { code: null, name: '전체' },
      { code: 'REGISTERED', name: '리포트 발행' },
      { code: 'COMPLETED', name: '리포트 처리완료' },
      { code: 'CANCELED', name: '리포트 발행취소' },
    ],
    reportAllYesNo: [
      { code: null, name: '전체' },
      { code: 'YES', name: '있음' },
      { code: 'NO', name: '없음' },
    ],
  },
};

const filterAllEmptyResponse: TypeApiResponse<TypeGet3plFilterAllResponse> = {
  data: {
    codeTypes: [],
    clusters: [],
    warehouses: [],
    dateTypes: [],
    statuses: [],
    releaseCodes: [],
    xdockAllYesNo: [],
    disallowAllYesNo: [],
    suppliers: [],
    vehicleSizes: [],
    reportTaskCodes: [],
    reportStatuses: [],
    reportAllYesNo: [],
  },
};

registerMockAPI<TypeApiResponse<TypeGet3plFilterAllResponse> | TypeApiResponse<TypeReturnDisposalFilterAllResponse>>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/filter/all',
  // 필터 옵션은 screenName 값만 다른 단일 엔드포인트. 같은 키로 두 번 등록하면 나중 등록이 시나리오 목록을 덮으므로 여기서만 등록한다.
  label: [
    '입고/입고요청/필터옵션',
    '입고/입고현황/필터옵션',
    '입고/입고현황3PL/필터옵션',
    '입고/입고리포트/필터옵션',
    '입고/회송폐기/필터옵션',
  ],
  scenarios: [
    {
      name: '정상 응답',
      response: ({ searchParams }) =>
        searchParams.get('screenName') === RETURN_DISPOSAL_SCREEN_NAME
          ? returnDisposalFilterAllResponse
          : filterAllNormalResponse,
    },
    { name: '빈 옵션', response: filterAllEmptyResponse },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: 'Error Occured By Not Valid Parameters' } },
      status: 400,
    },
  ],
});
